<template>
  <div 
    ref="rendererRef" 
    class="markdown-renderer-container" 
    :class="[format, { 'is-dark': isDark }]"
  >
    <!-- Direct HTML Rendering (Sanitized) -->
    <div 
      v-if="format === 'html'" 
      class="html-content" 
      v-html="sanitizedHTML"
    ></div>

    <!-- Parsed Markdown Rendering (Sanitized + Compiled) -->
    <div 
      v-else 
      class="markdown-content cms-content" 
      v-html="sanitizedMarkdownHTML"
      @click="handleContainerClick"
    ></div>

    <!-- Fullscreen Diagram Dialog -->
    <Dialog
      v-model:visible="isFullscreenOpen"
      modal
      dismissableMask
      header="Diagram Viewer"
      class="fullscreen-diagram-dialog"
      :style="{ width: '90vw', height: '85vh' }"
      :baseZIndex="2100"
      appendTo="body"
    >
      <div class="fullscreen-dialog-toolbar">
        <button 
          type="button" 
          class="mermaid-tool-btn" 
          @click="toggleFullscreenFit()"
        >
          <i :class="['pi', fitFullscreen ? 'pi-search-plus' : 'pi-arrows-alt']"></i>
          {{ fitFullscreen ? 'View Actual Size' : 'Fit Viewport' }}
        </button>
      </div>
      <div 
        class="fullscreen-diagram-container" 
        :class="fitFullscreen ? 'fit' : 'natural'"
        v-html="activeFullscreenBlockHtml"
      ></div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import mermaid from 'mermaid'
import elkLayouts from '@mermaid-js/layout-elk';
import Dialog from 'primevue/dialog'
import { useTheme } from '@/composables/useTheme'

interface Props {
  content: string
  format?: 'html' | 'markdown'
}

const props = withDefaults(defineProps<Props>(), {
  format: 'html'
})

const rendererRef = ref<HTMLElement | null>(null)
const { isDark } = useTheme()

// Fullscreen diagram viewer state
const isFullscreenOpen = ref(false)
const activeFullscreenBlockHtml = ref<string | null>(null)
const fitFullscreen = ref(true)

// Initialize Mermaid configurations
mermaid.initialize({
  startOnLoad: false,
  securityLevel: 'loose',
  theme: isDark.value ? 'dark' : 'default',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  layout: 'elk',       // ELK produces superior hierarchical layouts vs dagre
  flowchart: {
    useMaxWidth: false  // Let container CSS control width, not Mermaid
  }
} as any)

mermaid.registerLayoutLoaders(elkLayouts);

// Configure marked to output custom container structures for Mermaid code blocks
marked.use({
  breaks: true,
  gfm: true,
  renderer: {
    code({ text, lang }) {
      if (lang === 'mermaid') {
        return `
          <div class="mermaid-diagram-block">
            <div class="mermaid-toolbar">
              <button type="button" class="mermaid-tool-btn scroll-btn" title="Toggle between fit-to-width and natural-scroll view">
                <i class="pi pi-arrows-h"></i> <span class="btn-label">Full Size</span>
              </button>
              <button type="button" class="mermaid-tool-btn fullscreen-btn" title="Open Fullscreen Viewer">
                <i class="pi pi-window-maximize"></i> Fullscreen
              </button>
            </div>
            <div class="mermaid-scroll-wrapper">
              <pre class="mermaid" title="Click to open fullscreen">${text}</pre>
            </div>
          </div>
        \n`
      }
      return `<pre><code class="language-${lang || 'none'}">${text}</code></pre>\n`
    }
  }
})

// Sanitized raw HTML for html content types (fixes unbalanced layout tags and strips injection)
const sanitizedHTML = computed(() => {
  if (!props.content) return ''
  return DOMPurify.sanitize(props.content, {
    USE_PROFILES: { html: true }
  })
})

// Compiled and secure Markdown content
const sanitizedMarkdownHTML = computed(() => {
  if (!props.content) return ''
  try {
    const rawCompiled = marked.parse(props.content) as string
    
    // Process blockquote alerts in HTML post-processing
    const processedAlerts = rawCompiled.replace(/<blockquote>\s*<p>\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*(?:<br\s*\/?>)?\s*([\s\S]*?)<\/blockquote>/gi, (_match, type, content) => {
      const cleanType = type.toUpperCase();
      let iconClass = 'pi-info-circle';
      if (cleanType === 'TIP') iconClass = 'pi-lightbulb';
      else if (cleanType === 'IMPORTANT') iconClass = 'pi-exclamation-circle';
      else if (cleanType === 'WARNING') iconClass = 'pi-exclamation-triangle';
      else if (cleanType === 'CAUTION') iconClass = 'pi-shield';

      return `
        <div class="markdown-alert markdown-alert-${cleanType.toLowerCase()}">
          <p class="markdown-alert-title">
            <i class="pi ${iconClass}"></i>
            <span>${cleanType}</span>
          </p>
          <div class="markdown-alert-content">
            <p>${content}
          </div>
        </div>
      `;
    });

    // Purify compiled markup from potential script injections or malformed tags
    const cleanCompiled = DOMPurify.sanitize(processedAlerts, {
      USE_PROFILES: { html: true }
    })
    
    // Process external standard target hooks
    return cleanCompiled.replace(/<a /g, '<a target="_blank" rel="noopener" ')
  } catch (err) {
    console.error('Markdown compiler error:', err)
    return props.content
  }
})

// High-performance asynchronous Mermaid renderer
let activeObserver: IntersectionObserver | null = null

const runMermaidRenderer = async () => {
  await nextTick()
  if (!rendererRef.value) return

  const diagramBlocks = rendererRef.value.querySelectorAll('.mermaid')
  if (diagramBlocks.length === 0) return

  // Disconnect any existing observer to prevent leaks or duplicate bindings
  if (activeObserver) {
    activeObserver.disconnect()
    activeObserver = null
  }

  try {
    // 1. Wait for webfonts to load completely to prevent dimensional labels clip
    if (document.fonts) {
      await document.fonts.ready
    }

    // 2. Re-initialize with current theme
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: 'loose',
      theme: isDark.value ? 'dark' : 'default',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      layout: 'elk',
      flowchart: {
        useMaxWidth: false
      }
    } as any)

    // 3. Observe elements and render them when they become visible and have stable layout dimensions
    activeObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const block = entry.target as HTMLElement
          if (activeObserver) {
            activeObserver.unobserve(block)
          }

          // We wait until the element is actually visible and has a width
          const renderWhenReady = () => {
            const rect = block.getBoundingClientRect()
            if (rect.width > 0) {
              // Wait a small timeout to allow modal transitions to complete fully
              setTimeout(async () => {
                // Double check it's still in DOM and has width
                if (!block.isConnected) return
                
                // Skip if already processed in this render pass
                if (block.getAttribute('data-processed') === 'true') return

                // Retrieve original text
                let originalText = block.getAttribute('data-original-text')
                if (!originalText) {
                  originalText = block.textContent || ''
                  block.setAttribute('data-original-text', originalText)
                }

                const blockId = `mermaid-render-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

                try {
                  // Validate code structure first
                  await mermaid.parse(originalText)
                  
                  // Render block diagram
                  const { svg } = await mermaid.render(blockId, originalText)
                  
                  // Inject compiled SVG
                  block.innerHTML = svg

                  // Store the native SVG width as a data attribute for the fullscreen viewer,
                  // but do NOT force it inline — let CSS max-width: 100% handle inline scaling.
                  const svgEl = block.querySelector('svg')
                  if (svgEl) {
                    // Determine native viewBox dimensions before clearing styles
                    const styleAttr = svgEl.getAttribute('style') || ''
                    const maxWidthMatch = styleAttr.match(/max-width:\s*([\d.]+)px/)
                    if (maxWidthMatch) {
                      block.setAttribute('data-native-width', maxWidthMatch[1])
                    } else {
                      const viewBox = svgEl.getAttribute('viewBox') || ''
                      const vbParts = viewBox.trim().split(/\s+/)
                      if (vbParts.length === 4) {
                        block.setAttribute('data-native-width', vbParts[2])
                      }
                    }

                    // Clear Mermaid's inline sizing and apply fit-mode via setProperty.
                    // NOTE: .mermaid :deep(svg) scoped CSS doesn't reach v-html content,
                    // so we use JS with 'important' to ensure the initial fit state takes effect.
                    svgEl.style.removeProperty('max-width')
                    svgEl.style.removeProperty('width')
                    svgEl.style.setProperty('width', '100%', 'important')
                    svgEl.style.setProperty('height', 'auto', 'important')
                    svgEl.style.setProperty('max-width', '100%', 'important')
                    svgEl.style.setProperty('display', 'block', 'important')
                  }

                  block.setAttribute('data-processed', 'true')
                  block.classList.add('render-success')
                } catch (syntaxErr: any) {
                  console.warn('[Mermaid Parser] Rendering warning caught:', syntaxErr)
                  // Inject clean non-intrusive alert container instead of crashing
                  block.innerHTML = `
                    <div class="mermaid-error-callout">
                      <i class="pi pi-exclamation-triangle"></i>
                      <span><strong>Mermaid Syntax Error:</strong> Adjust diagram definitions to resolve rendering bounds.</span>
                    </div>
                  `
                  block.setAttribute('data-processed', 'true')
                  block.classList.add('render-error')
                }
              }, 250) // Wait 250ms for animations/transitions to finish
            } else {
              // If not yet ready, check again on next animation frame
              requestAnimationFrame(renderWhenReady)
            }
          }
          renderWhenReady()
        }
      })
    }, {
      root: null, // viewport
      threshold: 0.05 // Trigger as soon as a small part is visible
    })

    diagramBlocks.forEach((block) => {
      // If we are re-running due to a theme or content watch change, reset the block state
      const originalText = block.getAttribute('data-original-text')
      if (originalText && block.getAttribute('data-processed') === 'true') {
        block.removeAttribute('data-processed')
        block.textContent = originalText
        block.classList.remove('render-success', 'render-error')
      }
      
      if (activeObserver) {
        activeObserver.observe(block)
      }
    })
  } catch (err) {
    console.error('Failed to parse diagrams batch:', err)
  }
}

// Watching state triggers for preview syncing
watch(() => props.content, runMermaidRenderer)
watch(() => props.format, runMermaidRenderer)
watch(isDark, () => {
  // Re-run compiler to draw appropriate dark/light layouts dynamically
  runMermaidRenderer()
})

onMounted(() => {
  runMermaidRenderer()
})

onBeforeUnmount(() => {
  if (activeObserver) {
    activeObserver.disconnect()
    activeObserver = null
  }
})

/**
 * Open fullscreen modal for a given mermaid diagram block.
 * Strips Mermaid's hardcoded sizing so CSS fit mode takes control initially.
 */
function openFullscreenForBlock(block: Element) {
  const svgEl = block.querySelector('.mermaid svg')
  if (!svgEl) return

  // Clone and strip all Mermaid-injected size constraints
  const svgClone = svgEl.cloneNode(true) as SVGElement
  svgClone.removeAttribute('width')
  svgClone.removeAttribute('height')
  svgClone.removeAttribute('style')
  // CSS .fullscreen-diagram-container.fit svg handles initial display

  activeFullscreenBlockHtml.value = svgClone.outerHTML
  isFullscreenOpen.value = true
  fitFullscreen.value = true // start in fit mode
}

/**
 * Toggle between fit-to-viewport and actual pixel-size modes.
 * CSS `width: auto` defaults SVGs to 300px, so we read the viewBox
 * and apply explicit pixel dimensions via JS for the "actual size" mode.
 */
async function toggleFullscreenFit() {
  fitFullscreen.value = !fitFullscreen.value
  await nextTick()

  const svgEl = document.querySelector('.fullscreen-diagram-container svg') as SVGElement | null
  if (!svgEl) return

  if (fitFullscreen.value) {
    // Fit mode: scale to fill the container width
    // Use setProperty with 'important' to override CSS !important rules on the SVG
    svgEl.style.setProperty('width', '100%', 'important')
    svgEl.style.setProperty('height', 'auto', 'important')
    svgEl.style.setProperty('max-width', '100%', 'important')
  } else {
    // Actual size: render at the SVG's native viewBox pixel dimensions
    const vb = svgEl.getAttribute('viewBox') || ''
    const parts = vb.trim().split(/\s+/)
    if (parts.length === 4) {
      const nativeW = parseFloat(parts[2])
      const nativeH = parseFloat(parts[3])
      svgEl.style.setProperty('width', `${nativeW}px`, 'important')
      svgEl.style.setProperty('height', `${nativeH}px`, 'important')
      svgEl.style.setProperty('max-width', 'none', 'important')
    }
  }
}

function handleContainerClick(event: MouseEvent) {
  const target = event.target as HTMLElement

  // 1. Scroll/Fit toggle — switches between scale-to-fit and native-size-with-scroll.
  // CSS `width: auto` on SVG defaults to 300px (browser spec for replaced content),
  // so we read the viewBox and apply explicit pixel dimensions via JS, same as toggleFullscreenFit().
  const scrollBtn = target.closest('.scroll-btn')
  if (scrollBtn) {
    const block = scrollBtn.closest('.mermaid-diagram-block')
    if (block) {
      const isNowScroll = block.classList.toggle('is-scroll-mode')
      const label = scrollBtn.querySelector('.btn-label')
      if (label) label.textContent = isNowScroll ? 'Fit View' : 'Full Size'

      const svgEl = block.querySelector('.mermaid svg') as SVGElement | null
      if (svgEl) {
        if (isNowScroll) {
          // Scroll mode: render SVG at its native viewBox pixel dimensions.
          // Use setProperty with 'important' to override CSS !important rules on the SVG.
          const vb = svgEl.getAttribute('viewBox') || ''
          const parts = vb.trim().split(/\s+/)
          if (parts.length === 4) {
            svgEl.style.setProperty('width', `${parseFloat(parts[2])}px`, 'important')
            svgEl.style.setProperty('height', `${parseFloat(parts[3])}px`, 'important')
            svgEl.style.setProperty('max-width', 'none', 'important')
          }
        } else {
          // Fit mode: scale to fill the diagram block width
          svgEl.style.setProperty('width', '100%', 'important')
          svgEl.style.setProperty('height', 'auto', 'important')
          svgEl.style.setProperty('max-width', '100%', 'important')
        }
      }
    }
    return
  }

  // 2. Fullscreen button
  const fullscreenBtn = target.closest('.fullscreen-btn')
  if (fullscreenBtn) {
    const block = fullscreenBtn.closest('.mermaid-diagram-block')
    if (block) openFullscreenForBlock(block)
    return
  }

  // 3. Click on the diagram itself opens fullscreen
  const mermaidEl = target.closest('.mermaid')
  if (mermaidEl && mermaidEl.getAttribute('data-processed') === 'true') {
    const block = mermaidEl.closest('.mermaid-diagram-block')
    if (block) openFullscreenForBlock(block)
    return
  }

  // 4. Standard internal link routing
  const anchor = target.closest('a')
  if (anchor) {
    const href = anchor.getAttribute('href')
    if (href && href.startsWith('/')) {
      event.preventDefault()
    }
  }
}
</script>

<style scoped>
.markdown-renderer-container {
  width: 100%;
  line-height: 1.7;
}

.mermaid-diagram-block {
  background: var(--color-bg-secondary, #1e293b);
  border: 1px solid var(--color-border, #334155);
  border-radius: var(--radius-lg, 12px);
  padding: var(--spacing-md, 16px);
  margin: var(--spacing-md, 16px) 0;
  display: block;
  box-shadow: var(--shadow-sm);
  transition: background var(--transition-normal);
  position: relative;
}

.mermaid-scroll-wrapper {
  width: 100%;
  overflow-x: auto;
  padding-top: var(--spacing-lg, 24px); /* Space for floating toolbar */
}

.mermaid-toolbar {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: var(--spacing-xs, 8px);
  z-index: 10;
  opacity: 0.6;
  transition: opacity var(--transition-fast, 0.2s);
}

.mermaid-diagram-block:hover .mermaid-toolbar {
  opacity: 1;
}

.mermaid-tool-btn {
  background: var(--color-bg-tertiary, #0f172a);
  color: var(--color-text-secondary, #94a3b8);
  border: 1px solid var(--color-border, #334155);
  border-radius: var(--radius-md, 6px);
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all var(--transition-fast, 0.2s);
}

.mermaid-tool-btn:hover {
  background: var(--color-bg-primary, #1e293b);
  color: var(--color-accent, #6366f1);
  border-color: var(--color-accent, #6366f1);
}

.mermaid {
  width: 100%;
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow-x: hidden;
  display: block;
  text-align: center;
  cursor: zoom-in; /* hint that click opens fullscreen */
}

/* Default (fit) mode: SVG scales to fill container width */
.mermaid :deep(svg) {
  max-width: 100% !important;
  width: 100% !important;
  height: auto !important;
  display: block !important;
  margin: 0 auto !important;
}

/* Scroll mode: SVG renders at native size, wrapper scrolls horizontally */
.mermaid-diagram-block.is-scroll-mode .mermaid {
  overflow-x: auto;
  cursor: default;
}
.mermaid-diagram-block.is-scroll-mode .mermaid :deep(svg) {
  width: auto !important;
  max-width: none !important;
}

/*
  Make Mermaid's injected SVG background rect transparent so the
  container's background color always controls the diagram background.
  This ensures inline and fullscreen modal look identical.
*/
:deep(.mermaid svg > rect:first-child),
:deep(.fullscreen-diagram-container svg > rect:first-child) {
  fill: transparent !important;
}

/* Override default yellow/orange subgraph backgrounds and borders to match theme colors */
:deep(.cluster rect) {
  fill: var(--color-bg-tertiary, #111117) !important;
  stroke: var(--color-border, rgba(214, 220, 220, 0.15)) !important;
}

:deep(.cluster rect.subgraph-title-rect) {
  fill: var(--color-bg-secondary, #0a0a0f) !important;
}

:deep(.mermaid-error-callout) {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm, 8px);
  padding: var(--spacing-md, 12px);
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  color: #ef4444;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.85rem;
  line-height: 1.4;
  width: 100%;
}

:deep(.mermaid-error-callout) i {
  font-size: 1.1rem;
  flex-shrink: 0;
}

:deep(pre) {
  background: var(--color-bg-tertiary, #0f172a);
  border: 1px solid var(--color-border, #1e293b);
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 0.9rem;
}

:deep(code:not(pre code)) {
  background: var(--color-bg-tertiary, #0f172a);
  color: var(--color-accent, #6366f1);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
}

:deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
}

:deep(th), :deep(td) {
  border: 1px solid var(--color-border, #334155);
  padding: 10px 14px;
}

:deep(th) {
  background: var(--color-bg-secondary, #1e293b);
  font-weight: 600;
}

/* GitHub Style Callout Alerts */
:deep(.markdown-alert) {
  padding: var(--spacing-md, 12px) var(--spacing-lg, 16px);
  margin: var(--spacing-md, 16px) 0;
  border-left: 4px solid var(--border-color, var(--color-border));
  border-radius: 0 var(--radius-md, 8px) var(--radius-md, 8px) 0;
  background: var(--bg-color, var(--color-bg-secondary));
}

:deep(.markdown-alert-title) {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs, 6px);
  font-weight: 700;
  font-size: 0.85rem;
  margin: 0 0 var(--spacing-xs, 6px) 0 !important;
  color: var(--text-color, var(--color-text-primary));
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

:deep(.markdown-alert-title i) {
  font-size: 1rem;
}

:deep(.markdown-alert-content) {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}

:deep(.markdown-alert-content p) {
  margin: 0 !important;
}

/* Alert Theme Varieties */
:deep(.markdown-alert-note) {
  --border-color: #3b82f6; /* Blue */
  --bg-color: rgba(59, 130, 246, 0.04);
  --text-color: #3b82f6;
}

:deep(.markdown-alert-tip) {
  --border-color: #10b981; /* Green */
  --bg-color: rgba(16, 185, 129, 0.04);
  --text-color: #10b981;
}

:deep(.markdown-alert-important) {
  --border-color: #8b5cf6; /* Violet */
  --bg-color: rgba(139, 92, 246, 0.04);
  --text-color: #8b5cf6;
}

:deep(.markdown-alert-warning) {
  --border-color: #f59e0b; /* Amber */
  --bg-color: rgba(245, 158, 11, 0.04);
  --text-color: #f59e0b;
}

:deep(.markdown-alert-caution) {
  --border-color: #ef4444; /* Red */
  --bg-color: rgba(239, 68, 68, 0.04);
  --text-color: #ef4444;
}
</style>

<style>
/* Unscoped Styles for Teleported Fullscreen Diagram Dialog */
.fullscreen-diagram-dialog {
  background: var(--color-bg-primary, #0f172a) !important;
  border: 1px solid var(--color-border, #334155) !important;
  border-radius: var(--radius-xl, 16px) !important;
  box-shadow: var(--shadow-2xl) !important;
}

.fullscreen-diagram-dialog .p-dialog-header {
  background: var(--color-bg-secondary, #1e293b) !important;
  color: var(--color-text-primary, #f8fafc) !important;
  border-bottom: 1px solid var(--color-border, #334155) !important;
  padding: 1rem 1.5rem !important;
  border-top-left-radius: var(--radius-xl, 16px) !important;
  border-top-right-radius: var(--radius-xl, 16px) !important;
}

.fullscreen-diagram-dialog .p-dialog-content {
  padding: 0 !important;
  /* Match the inline diagram-block background so connectors/edges are equally visible */
  background: var(--color-bg-secondary, #1e293b) !important;
  display: flex;
  flex-direction: column;
  overflow: hidden !important;
  height: 100% !important;
  border-bottom-left-radius: var(--radius-xl, 16px) !important;
  border-bottom-right-radius: var(--radius-xl, 16px) !important;
}

.fullscreen-dialog-toolbar {
  padding: 10px 16px;
  border-bottom: 1px solid var(--color-border, #334155);
  background: var(--color-bg-secondary, #1e293b);
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

.fullscreen-diagram-container {
  flex-grow: 1;
  width: 100%;
  overflow: auto;
  padding: 24px;
  box-sizing: border-box;
}

:deep(.fullscreen-diagram-container svg) {
  display: block !important;
  margin: 0 auto !important;
}

/* Fit mode: fill the full available width, height scales proportionally */
:deep(.fullscreen-diagram-container.fit svg) {
  width: 100% !important;
  height: auto !important;
  max-width: 100% !important;
}

/* Natural/actual-size mode: render at native SVG size, scroll if needed */
:deep(.fullscreen-diagram-container.natural svg) {
  width: auto !important;
  height: auto !important;
  max-width: none !important;
}
</style>
